/**
 * Space Management API
 *
 * Fetches units grouped by area with allocation counts and
 * contract aircraft counts for the Space Management page.
 *
 * Uses three parallel Supabase queries composed client-side:
 * 1. Units with area info and active lease details
 * 2. Allocation counts per unit/area (placed aircraft)
 * 3. Contract aircraft counts per unit (assigned aircraft)
 */

import { supabase } from '@/lib/supabase'
import type {
  SpaceManagementArea,
  SpaceManagementUnit,
} from '@/types/space-management'
import type { AreaType, HangarStatus } from '@/types/hangars'
import type { UnitStatus } from '@/types/units'

// ============================================
// QUERY 1: Units with area info + lease details
// ============================================

async function fetchUnitsWithLeaseInfo(locationId: string) {
  const { data, error } = await supabase
    .from('units')
    .select(`
      id, unit_code, name, area_id, location_id,
      type, capacity_strategy,
      usable_sqft, interior_width, interior_depth,
      door_width, door_height, door_type,
      max_wingspan, max_length, max_tail_height,
      status, is_leasable, current_lease_id,
      default_monthly_rate, default_transient_rate,
      rate_type, pricing_override, unit_monthly_rate, unit_rate_per_sqft,
      areas:area_id (
        id, name, area_code, type, status,
        total_footprint_sqft, exterior_width, exterior_depth,
        monthly_rate_private, monthly_rate_shared, transient_rate_nightly
      ),
      lease_contracts:current_lease_id (
        monthly_rate, status,
        clients:client_id (
          company_name, first_name, last_name, client_type
        )
      )
    `)
    .eq('location_id', locationId)
    .is('deleted_at', null)
    .order('area_id')
    .order('sequence')

  if (error) throw error
  return data ?? []
}

// ============================================
// QUERY 2: Allocation counts (placed aircraft)
// ============================================

interface AllocationCounts {
  unitCounts: Map<string, number>
  areaCounts: Map<string, number>
  unitOccupiedSqft: Map<string, number>
}

async function fetchAllocationCounts(locationId: string): Promise<AllocationCounts> {
  const { data, error } = await supabase
    .from('allocations')
    .select(`
      unit_id, area_id,
      aircraft!aircraft_id (
        wingspan, length,
        aircraft_types!aircraft_type_id ( footprint_sqft )
      )
    `)
    .eq('location_id', locationId)
    .eq('status', 'active')
    .is('deleted_at', null)

  if (error) throw error

  const unitCounts = new Map<string, number>()
  const areaCounts = new Map<string, number>()
  const unitOccupiedSqft = new Map<string, number>()

  for (const row of data ?? []) {
    if (row.unit_id) {
      unitCounts.set(row.unit_id, (unitCounts.get(row.unit_id) ?? 0) + 1)

      // Compute footprint for this allocation's aircraft
      const ac = row.aircraft as unknown as {
        wingspan: number | null
        length: number | null
        aircraft_types: { footprint_sqft: number | null } | null
      } | null
      const sqft = ac?.aircraft_types?.footprint_sqft
        ?? ((ac?.wingspan ?? 0) * (ac?.length ?? 0))
      unitOccupiedSqft.set(row.unit_id, (unitOccupiedSqft.get(row.unit_id) ?? 0) + sqft)
    } else if (row.area_id) {
      // Area-level allocations (e.g., community hangars)
      areaCounts.set(row.area_id, (areaCounts.get(row.area_id) ?? 0) + 1)
    }
  }

  return { unitCounts, areaCounts, unitOccupiedSqft }
}

// ============================================
// QUERY 3: Contract aircraft counts (assigned)
// ============================================

async function fetchContractAircraftCounts(
  locationId: string
): Promise<Map<string, number>> {
  const { data, error } = await supabase
    .from('contract_aircraft')
    .select(`
      id,
      lease_contracts:lease_contract_id (
        unit_id,
        location_id,
        status
      )
    `)
    .eq('status', 'active')

  if (error) throw error

  const unitCounts = new Map<string, number>()

  for (const row of data ?? []) {
    const lease = row.lease_contracts as unknown as {
      unit_id: string | null
      location_id: string | null
      status: string
    } | null

    if (
      lease &&
      lease.location_id === locationId &&
      lease.unit_id &&
      ['active', 'expiring'].includes(lease.status)
    ) {
      unitCounts.set(lease.unit_id, (unitCounts.get(lease.unit_id) ?? 0) + 1)
    }
  }

  return unitCounts
}

// ============================================
// HELPERS
// ============================================

function computeClientName(
  client: {
    company_name: string | null
    first_name: string | null
    last_name: string | null
    client_type: string | null
  } | null
): string | null {
  if (!client) return null
  if (client.company_name) return client.company_name
  const parts = [client.first_name, client.last_name].filter(Boolean)
  return parts.length > 0 ? parts.join(' ') : null
}

// ============================================
// COMPOSER: Combine all queries
// ============================================

/**
 * Fetch all space management data for a location.
 * Runs three queries in parallel and composes the result into
 * SpaceManagementArea[] with computed aggregates.
 */
export async function fetchSpaceManagementData(
  locationId: string
): Promise<SpaceManagementArea[]> {
  const [unitsData, allocationData, contractData] = await Promise.all([
    fetchUnitsWithLeaseInfo(locationId),
    fetchAllocationCounts(locationId),
    fetchContractAircraftCounts(locationId),
  ])

  // Group units by area
  const areaMap = new Map<string, SpaceManagementArea>()

  for (const row of unitsData) {
    const area = row.areas as unknown as {
      id: string
      name: string
      area_code: string
      type: AreaType
      status: HangarStatus
      total_footprint_sqft: number
      exterior_width: number
      exterior_depth: number
      monthly_rate_private: number | null
      monthly_rate_shared: number | null
      transient_rate_nightly: number | null
    } | null

    if (!area) continue

    const lease = row.lease_contracts as unknown as {
      monthly_rate: number | null
      status: string
      clients: {
        company_name: string | null
        first_name: string | null
        last_name: string | null
        client_type: string | null
      } | null
    } | null

    const unit: SpaceManagementUnit = {
      id: row.id,
      unit_code: row.unit_code,
      name: row.name,
      area_id: row.area_id,
      location_id: row.location_id,
      type: row.type as SpaceManagementUnit['type'],
      capacity_strategy: row.capacity_strategy as SpaceManagementUnit['capacity_strategy'],
      usable_sqft: row.usable_sqft,
      interior_width: row.interior_width,
      interior_depth: row.interior_depth,
      door_width: row.door_width,
      door_height: row.door_height,
      door_type: row.door_type as SpaceManagementUnit['door_type'],
      max_wingspan: row.max_wingspan,
      max_length: row.max_length,
      max_tail_height: row.max_tail_height,
      status: (row.status || 'available') as UnitStatus,
      is_leasable: row.is_leasable,
      current_lease_id: row.current_lease_id,
      default_monthly_rate: row.default_monthly_rate,
      default_transient_rate: row.default_transient_rate,
      rate_type: row.rate_type as SpaceManagementUnit['rate_type'],
      pricing_override: row.pricing_override,
      unit_monthly_rate: row.unit_monthly_rate,
      unit_rate_per_sqft: row.unit_rate_per_sqft,
      // Computed counts
      placed_aircraft_count: allocationData.unitCounts.get(row.id) ?? 0,
      assigned_aircraft_count: contractData.get(row.id) ?? 0,
      occupied_sqft: allocationData.unitOccupiedSqft.get(row.id) ?? 0,
      // Lease info
      lease_monthly_rate: lease?.monthly_rate ?? null,
      lease_client_name: computeClientName(lease?.clients ?? null),
      lease_status: lease?.status ?? null,
      // Parent area
      area_name: area.name,
      area_code: area.area_code,
      area_type: area.type,
    }

    // Get or create area group
    if (!areaMap.has(area.id)) {
      areaMap.set(area.id, {
        id: area.id,
        name: area.name,
        area_code: area.area_code,
        type: area.type,
        status: area.status,
        total_footprint_sqft: area.total_footprint_sqft,
        exterior_width: area.exterior_width,
        exterior_depth: area.exterior_depth,
        monthly_rate_private: area.monthly_rate_private,
        monthly_rate_shared: area.monthly_rate_shared,
        transient_rate_nightly: area.transient_rate_nightly,
        units: [],
        total_units: 0,
        total_usable_sqft: 0,
        total_placed_aircraft: 0,
        total_assigned_aircraft: 0,
        units_available: 0,
        units_leased: 0,
        units_maintenance: 0,
      })
    }

    areaMap.get(area.id)!.units.push(unit)
  }

  // Compute aggregates
  for (const area of areaMap.values()) {
    area.total_units = area.units.length
    area.total_usable_sqft = area.units.reduce(
      (sum, u) => sum + (u.usable_sqft ?? 0),
      0
    )
    area.total_placed_aircraft =
      area.units.reduce((sum, u) => sum + u.placed_aircraft_count, 0) +
      (allocationData.areaCounts.get(area.id) ?? 0)
    area.total_assigned_aircraft = area.units.reduce(
      (sum, u) => sum + u.assigned_aircraft_count,
      0
    )
    area.units_available = area.units.filter(
      (u) => u.status === 'available'
    ).length
    area.units_leased = area.units.filter(
      (u) => u.status === 'leased'
    ).length
    area.units_maintenance = area.units.filter(
      (u) => u.status === 'maintenance'
    ).length
  }

  return Array.from(areaMap.values()).sort((a, b) =>
    a.name.localeCompare(b.name)
  )
}
